import SwipeUpModal from 'components/SwipeUpModal';
import { actionSetWorkOrderOpActionLoading } from 'lib/actions';
import useSelectActivity from 'lib/hooks/useSelectActivity';
import { getHasOpenJob } from 'lib/selectors/getHasOpenJob';
import useCreateActivitySetWithWorkOrder from 'pages/SelectJob/useCreateActivitySetWithWorkOrder';
import React, { useCallback, useMemo } from 'react';
import { useJobDisplayName } from 'lib/hooks/useJobDisplayName';
import { useDispatch, useSelector } from 'react-redux';
import { getMachine } from 'lib/selectors/getMachine';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { useTranslation } from 'react-i18next';

import useChangeActivities from 'components/LaborTicket/hooks/useChangeActivities';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import useLookupActivityTypeName from 'lib/hooks/useLookupActivityTypeName';
import useRequestJobStartAt from 'lib/hooks/useRequestJobStartAt';
import {
  findMatchingActivities,
  isActivityDisabled,
} from 'lib/utils/activityMatching';
import { getSwitchActivityTypesOptions } from 'lib/selectors/getSwitchActivityTypesOptions';
import { getOpenActivityTypeRef } from 'lib/selectors/getOpenActivity';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import { Table } from './Table';

const SelectActivity = () => {
  const { t } = useTranslation();
  const {
    selectActivityOpen,
    selectActivityOptions,
    closeSelectActivity,
  } = useSelectActivity();

  const dispatch = useDispatch();
  const hasOpenJob = useSelector(getHasOpenJob);
  const machine = useSelector(getMachine);

  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const activityTypes = useSelector(getSwitchActivityTypesOptions);
  const openActivityTypeRef = useSelector(getOpenActivityTypeRef);

  const { workOrderOp, partOperation } = selectActivityOptions || {};

  const selectedWorkOrderOp = workOrderOp || currentWorkOrderOp;

  const requestJobStartAt = useRequestJobStartAt();
  const requestCreateActivitySetWithWorkOrder = useCreateActivitySetWithWorkOrder(
    {
      machine,
      workOrderOp: selectedWorkOrderOp,
    }
  );
  const requestChangeActivities = useChangeActivities();
  const lookupActivityTypeName = useLookupActivityTypeName();
  const { getOrderOpDisplayName } = useJobDisplayName();

  // Memoize the order op display name to avoid re-rendering the component when the work order op changes
  const orderOpDisplayName = useMemo(() => {
    return getOrderOpDisplayName(selectedWorkOrderOp);
  }, [getOrderOpDisplayName, selectedWorkOrderOp]);

  const handleActivityTypeSelected = useCallback(
    async (activityTypeRef) => {
      if (hasOpenJob && selectedWorkOrderOp) {
        dispatch(actionSetWorkOrderOpActionLoading(true));
        await requestChangeActivities({
          pendingActivityTypeRef: activityTypeRef,
        });

        const activityTypeName = lookupActivityTypeName(activityTypeRef);
        if (currentWorkOrderOp) {
          throwSuccessToast(t('Switched Activity:', { activityTypeName }));
        }
      } else {
        try {
          const startAt = await requestJobStartAt();
          dispatch(actionSetWorkOrderOpActionLoading(true));

          await requestCreateActivitySetWithWorkOrder({
            activityTypeRef,
            partOperation,
            startAt,
          });
        } catch (error) {
          dispatch(actionSetWorkOrderOpActionLoading(false));
          return null;
        }
      }

      dispatch(actionSetWorkOrderOpActionLoading(false));
      return closeSelectActivity();
    },
    [
      hasOpenJob,
      selectedWorkOrderOp,
      dispatch,
      requestChangeActivities,
      lookupActivityTypeName,
      currentWorkOrderOp,
      t,
      requestJobStartAt,
      requestCreateActivitySetWithWorkOrder,
      partOperation,
      closeSelectActivity,
    ]
  );

  const handleBarcodeScan = useCallback(
    (barcode) => {
      const matches = findMatchingActivities(barcode, activityTypes);

      if (matches.length === 0) {
        throwErrorToast('Error: Activity not found');
        return;
      }

      if (matches.length > 1) {
        throwErrorToast('Error: Multiple activities found');
        return;
      }

      const matchedActivity = matches[0];

      if (
        isActivityDisabled(matchedActivity.activityTypeRef, openActivityTypeRef)
      ) {
        throwErrorToast('Error: Activity already active');
        return;
      }

      throwSuccessToast(`Activity matched: ${matchedActivity.name}`);
      handleActivityTypeSelected(matchedActivity.activityTypeRef);
    },
    [activityTypes, openActivityTypeRef, handleActivityTypeSelected]
  );

  useBarcodeScanHandler('select-activity-modal', handleBarcodeScan, {
    priority: BARCODE_HANDLER_PRIORITIES.SELECT_ACTIVITY_MODAL,
    enabled: selectActivityOpen,
  });

  const headerText =
    selectedWorkOrderOp && selectedWorkOrderOp?.workOrder
      ? `${selectedWorkOrderOp.workOrderId} | ${orderOpDisplayName}`
      : t('Select Activity');

  return (
    <SwipeUpModal
      isOpen={selectActivityOpen}
      header={headerText}
      onClose={() => {
        return closeSelectActivity();
      }}
      details={
        <Table
          workOrderOp={selectedWorkOrderOp}
          onActivityTypeSelect={handleActivityTypeSelected}
        />
      }
    />
  );
};

export { SelectActivity };
